import { useEffect, useRef } from 'react'
import textServices from 'services/text-services'
import useMediaQuery from 'hooks/use-media-query'
import { credencialesImage, credencialesVideo } from '../constants'
import useNearViewport from '../hooks/use-near-viewport'
import { useFloatingDecor } from '../hooks/use-scroll-parallax'

// Circular looping clip (no sound, no controls). The source is attached
// only when the circle approaches the viewport so below-the-fold clips do
// not download on first paint. React 17 does not render the muted
// attribute, so it is set by hand before play(); if autoplay is blocked
// the first frame simply stays still.
const CircleVideo = ({ src, className, children }: { src: string; className: string; children?: React.ReactNode }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const near = useNearViewport(wrapperRef)

  useEffect(() => {
    const video = videoRef.current
    if (!video || !near) return
    video.muted = true
    video.setAttribute('muted', '')
    video.play().catch(() => undefined)
  }, [src, near])

  return (
    <div ref={wrapperRef} className={`credenciales__process-circle ${className}`}>
      <video
        ref={videoRef}
        key={src}
        src={near ? src : undefined}
        autoPlay
        muted
        loop
        playsInline
        preload={near ? 'metadata' : 'none'}
      />
      {children}
    </div>
  )
}

// Brewing process (Figma desktop 5631:1604..1708 at y2857-4077 of the intro
// frame, mobile 5854:1297 y1218-2184): two circular videos with copy on
// alternating sides, laid out on the frame coordinates.
const ProcessSection = () => {
  const sectionRef = useRef<HTMLElement>(null)
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const size = isDesktop ? '600px' : '400px'

  useFloatingDecor(sectionRef, isDesktop)

  return (
    <section ref={sectionRef} className="credenciales__process">
      <span className="credenciales__line credenciales__process-line credenciales__process-line--first" aria-hidden="true" />
      <CircleVideo src={credencialesVideo(`vid_lupulos_${size}.mp4`)} className="credenciales__process-circle--hops" />
      <p className="credenciales__process-text credenciales__process-text--first">
        {textServices.findByText(
          'credenciales/process/text1Start',
          'Un proceso de elaboración especial que toma su tiempo, para que cada sorbo contenga ese '
        )}
        <strong>{textServices.findByText('credenciales/process/text1Bold', 'sabor único.')}</strong>
      </p>
      <span className="credenciales__line credenciales__process-line credenciales__process-line--second" aria-hidden="true" />
      <CircleVideo src={credencialesVideo(`vid_cerveza_${size}.mp4`)} className="credenciales__process-circle--beer">
      </CircleVideo>
      <p className="credenciales__process-text credenciales__process-text--second">
        {textServices.findByText('credenciales/process/text2Start', 'Acompañar con Heineken es asegurar un ')}
        <strong>{textServices.findByText('credenciales/process/text2Bold', 'momento de calidad.')}</strong>
      </p>
      <span
        className="credenciales__process-hop"
        data-float="hop"
        aria-hidden="true"
      >
        <img src={credencialesImage(isDesktop ? 'DESKTOP' : 'MOBILE', isDesktop ? 'flotante_lupulo_desk_3_2x' : 'flotante_lupulo_mob_3_2x')} alt="" loading="lazy" draggable={false} />
      </span>
    </section>
  )
}

export default ProcessSection
